import { Button, Container, List, ListItem, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Api, App, User } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { useStateValue } from "../../config/AppContext";
import { SelectClientsModal } from "../Modals";

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: "#ffffff",
    "& .MuiTypography-body1": {
      fontSize: 17,
      color: "#525252",
      margin: "30px auto",
      [theme.breakpoints.down("md")]: {
        marginTop: 15,
        paddingTop: 15,
      },
    },
    "& .MuiTypography-h4": {
      fontSize: 17,
      fontWeight: "bold",
      color: "##000000",
      margin: "20px 0",
      marginRight: 15,
    },
    "& .MuiTypography-h5": {
      margin: "30px 0",
      fontSize: 16,
      fontWeight: 500,
      color: "#525252",
      "& button": {
        float: "right",
        marginTop: -12,
        height: 45,
        width: 148,
        borderRadius: 4,
        backgroundColor: "#000000",
        fontSize: 14,
        fontWeight: 500,
        color: "#ffffff",
        textTransform: "none",
      },
    },
    "& .MuiList-root": {
      paddingTop: 0,
      paddingBottom: 0,
      height: 130,
      overflow: "auto",
      marginTop: -20,
      [theme.breakpoints.up("md")]: {
        marginBottom: "calc(16vh - 75px)",
      },
      "& .MuiListItem-root": {
        display: "block",
        width: "100%",
        padding: "2px 8px",
        "& .client-name": {
          margin: "auto",
          fontSize: 17,
          fontWeight: "bold",
          color: "#000000",
        },
        "& .client-email": {
          margin: "auto",
          fontSize: 16,
          color: "#525252",
        },
      },
    },
    "& textarea": {
      marginTop: -10,
      borderRadius: 3,
      padding: 15,
      width: "100%",
      height: 83,
      backgroundColor: "transparent",
      fontSize: 14,
      color: "#4A4A4A",
    },
    '& button[type="submit"]': {
      backgroundColor: "var(--text-primary)",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      fontSize: theme.spacing(2),
      fontWeight: 500,
      color: "#ffffff",
      textTransform: "none",
      // [theme.breakpoints.up('md')]: {
      //   position: 'absolute',
      //   width: '90%',
      //   bottom: 15,
      // },
    },
  },
}));

type ShareWorkoutsProps = {
  id: string;
  setLoading: (x: boolean) => void;
  loading: boolean;
  setShowWorkout: (x: string | null) => void;
  showWorkout: string | null;
  setShareWorkout: (x: string | null) => void;
};

export default function ShareWorkouts(props: ShareWorkoutsProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clients, Methods, metric } = useStateValue() as {
    clients: User.IUserClient[];
    Methods: App.Methods;
    metric: App.State["metric"];
  };
  const [toggled, setToggled] = React.useState(false);
  const [addClient, showAddClient] = React.useState(false);
  const [message, setMessage] = React.useState<string>();
  const [selectedClients, setSelectedClients] = React.useState<User.IUserClient["id"][] | null>(
    null
  );

  const handleSelectedClients = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    showAddClient(false);
    const formData = new FormData(e.currentTarget);
    const selectedClientList = formData.get("clients");
    selectedClientList && typeof selectedClientList === "string"
      ? setSelectedClients(JSON.parse(selectedClientList))
      : setSelectedClients(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: Api.Workout["shareParams"] = {
      id: props.id,
      to_self: toggled,
      to_customer_id: selectedClients ? selectedClients : [],
      message,
      is_metric: metric,
    };
    if (window.innerWidth > 700 && !props.loading) {
      const { showWorkout, setShareWorkout, setLoading, setShowWorkout } = props;
      setLoading(true);
      setShareWorkout(null);
      setShowWorkout(null);
      return Methods.shareWorkouts(params).then(() => {
        setLoading(false);
        return setShowWorkout(showWorkout);
      });
    } else if (window.innerWidth <= 700) {
      return Methods.shareWorkouts(params);
    }
  };

  return (
    <Container id="share-workout" className={classes.container}>
      <Typography variant="body1">
        {/* {t("Add recipients. If you want a copy sent to your email toggle on the selection below.")} */}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Typography variant="h4" onClick={() => setToggled(!toggled)} style={{ cursor: "pointer" }}>
          {t("Send PDF copy to my email")}
          <span
            className={toggled ? "toggle-container toggled" : "toggle-container"}
            onClick={() => setToggled(!toggled)}
          >
            <span className="toggle-button" />
          </span>
        </Typography>
        <Typography variant="h5">{t("Message")}</Typography>
        <textarea
          id="message"
          name="message"
          placeholder={t("Optional")}
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
        />
        <Typography variant="h5">
          {t("Added client list")}
          <Button variant="contained" onClick={() => showAddClient(true)}>
            {t("Add client")}
          </Button>
        </Typography>
        <Typography variant="h5">
          {t("File will be generated and sent to these clients")}
        </Typography>
        <List className="client-list">
          {selectedClients ? (
            clients.map((client) => {
              if (selectedClients.find((el) => el === client.id))
                return (
                  <ListItem key={uuid()}>
                    <Typography variant="body1" className="client-name" noWrap>
                      {client.first_name} {client.last_name}{" "}
                    </Typography>
                    <Typography variant="body2" className="client-email" noWrap>
                      {client.email}
                    </Typography>
                  </ListItem>
                );
              return <React.Fragment key={uuid()} />;
            })
          ) : (
            <></>
          )}
        </List>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={selectedClients === null && !toggled}
        >
          {t("Send")}
        </Button>
      </form>
      <SelectClientsModal
        show={addClient}
        onHide={() => showAddClient(!addClient)}
        onSubmit={handleSelectedClients}
      />
    </Container>
  );
}
